// Keystore self-service (AWS-PORT chunk 6): wrap-set operations on the
// identity bundle, all from an UNLOCKED browser. The wraps are alternative
// encryptions of the same identity secrets — regenerating or revoking one
// never touches the identity itself, the device list, or any record.
//
// Every write follows one pattern (`writeBundle`): fetch the bundle with its
// rev, refuse to touch one whose public keys aren't ours (the §9.6-style
// substitution guard), mutate ONLY `wraps` (or `inviteKeys`, for the invite
// flow that borrows it), custody-sign the canonical bundle, PUT conditioned
// on the rev — and re-apply on a 409 BUNDLE_CONFLICT (2026-10-08) instead of
// overwriting what another session wrote. The service accepts wrap-set-only
// changes under the chain custody key.
//
// Total-loss guard: an operation may never remove the LAST unlock method —
// at least one of (webauthn wrap, recovery wrap) must survive. Browser
// keystores and Windows DPAPI copies exist, but they are caches, not
// backstops.

import E2eeApi from '@/aws/e2ee/E2eeApi'
import { getSodium } from './sodium'
import { loadIdentity, clearIdentity } from './Keystore'
import { Nc1UnlockRequiredError } from '../E2EEErrors'
import { wrapSecrets } from './shared/kek.js'
import { generateRecoveryCode, recoveryCodeDisplay } from './shared/recovery.js'
import { b64, ub64 } from './shared/envelope.js'
import { canonicalizeBytes } from './shared/jcs.js'

/** Read-only wrap status for the manage UI. */
export async function wrapStatus (email) {
  email = String(email).toLowerCase()
  const bundle = (await E2eeApi.getIdentityBundle(email)).data
  return {
    passkeys: (bundle.wraps && bundle.wraps.webauthn) || [],
    hasRecovery: !!(bundle.wraps && bundle.wraps.recovery),
  }
}

/**
 * Shared preamble: unlocked identity + fetched bundle (with its rev) +
 * substitution guard. A caller that already holds the identity passes it as
 * `cached`; a key mismatch then throws `mismatch` instead of clearing the
 * keystore (the copy is the caller's, not the keystore's).
 */
async function openForWrapWrite (email, { cached: given = null, mismatch = null } = {}) {
  const sodium = await getSodium()
  const cached = given || await loadIdentity(email)
  if (!cached || !cached.secrets) throw new Nc1UnlockRequiredError(email)
  const { data: bundle, rev } = await E2eeApi.getIdentityBundle(email)
  if (bundle.idEncPk !== cached.secrets.id_enc_pk || bundle.idSignPk !== cached.secrets.id_sign_pk) {
    if (given) {
      throw new Error(mismatch || 'This browser\'s encryption keys are not the ones published for your account — unlock it again first.')
    }
    // Rotated since this browser cached its copy — stale secrets are useless;
    // clear and route to the unlock dialog.
    await clearIdentity(email)
    throw new Nc1UnlockRequiredError(email)
  }
  return { sodium, cached, bundle, rev }
}

async function publish (sodium, email, cached, value, baseRev = null) {
  const sig = b64(sodium, sodium.crypto_sign_detached(
    canonicalizeBytes(value), ub64(sodium, cached.secrets.id_sign_sk)))
  await E2eeApi.putIdentityBundle(email, value, sig, baseRev)
}

export const BUNDLE_WRITE_ATTEMPTS = 4

/**
 * Read-mutate-publish with optimistic concurrency (2026-10-08). `mutate(bundle,
 * { sodium, cached })` returns the new document, or null for "nothing to do".
 * The write carries the rev the bundle was read with; a 409 BUNDLE_CONFLICT
 * (another session changed the bundle in between — a passkey added in one
 * tab while an invite key is folded in another) re-reads and re-applies the
 * mutation, a few times, instead of silently overwriting the other change.
 * Resolves to { written, bundle } (the document as written, or as read when
 * the mutation declined).
 */
export async function writeBundle (email, mutate, { cached = null, mismatch = null, attempts = BUNDLE_WRITE_ATTEMPTS } = {}) {
  email = String(email).toLowerCase()
  let lastError = null
  for (let i = 0; i < attempts; i += 1) {
    const opened = await openForWrapWrite(email, { cached, mismatch })
    const value = await mutate(opened.bundle, { sodium: opened.sodium, cached: opened.cached })
    if (value === null) return { written: false, bundle: opened.bundle }
    try {
      await publish(opened.sodium, email, opened.cached, value, opened.rev)
      return { written: true, bundle: value }
    } catch (e) {
      if (!e || e.code !== 'BUNDLE_CONFLICT') throw e
      lastError = e
      // eslint-disable-next-line no-console
      console.log(`[nc1] identity bundle changed in another session - re-applying (${i + 1}/${attempts})`)
    }
  }
  throw lastError
}

const unlockMethodCount = (wraps) =>
  ((wraps && wraps.webauthn) || []).length + (wraps && wraps.recovery ? 1 : 0)

/**
 * Mint a fresh 160-bit recovery code and re-wrap `wraps.recovery` under it.
 * The old code stops working the moment the write lands. Returns
 * { code, display } — show ONCE, require acknowledgment, never store.
 */
export async function regenerateRecoveryCode (email) {
  email = String(email).toLowerCase()
  const sodium = await getSodium()
  const code = generateRecoveryCode(sodium)
  await writeBundle(email, (bundle, { cached }) => {
    const secretsBytes = new TextEncoder().encode(JSON.stringify(cached.secrets))
    return { ...bundle, wraps: { ...bundle.wraps, recovery: wrapSecrets(sodium, code, secretsBytes) } }
  })
  return { code, display: recoveryCodeDisplay(code) }
}

/**
 * Remove the recovery backstop. Refused when it is the last unlock method.
 */
export async function disableRecovery (email) {
  email = String(email).toLowerCase()
  await writeBundle(email, (bundle) => {
    if (!bundle.wraps || !bundle.wraps.recovery) return null
    if (unlockMethodCount(bundle.wraps) <= 1) {
      throw new Error('The recovery code is your only unlock method — set up a passkey before disabling it.')
    }
    const wraps = { ...bundle.wraps }
    delete wraps.recovery
    return { ...bundle, wraps }
  })
}

/**
 * Remove one passkey's wrap by credentialId. The passkey itself stays in the
 * user's authenticator/password manager (we can't delete it remotely) but
 * can no longer unlock anything. Refused when it is the last unlock method.
 */
export async function revokePasskey (email, credentialId) {
  email = String(email).toLowerCase()
  await writeBundle(email, (bundle) => {
    const existing = (bundle.wraps && bundle.wraps.webauthn) || []
    const remaining = existing.filter((w) => w.credentialId !== credentialId)
    if (remaining.length === existing.length) throw new Error('That passkey is not registered on this account.')
    if (remaining.length + (bundle.wraps.recovery ? 1 : 0) < 1) {
      throw new Error('This is your only unlock method — add another passkey or enable the recovery code first.')
    }
    const wraps = { ...bundle.wraps }
    if (remaining.length) wraps.webauthn = remaining
    else delete wraps.webauthn
    return { ...bundle, wraps }
  })
}
